import { ContractPerson } from '@ekt-group/general-purpose-api-interfaces/src/entities/contract-person.entity';
import { ChevronDown, ChevronRight, Pencil02, Trash04 } from '@untitled-ui/icons-react';
import { TableProps } from 'antd';
import dayjs from 'dayjs';
import { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

import Button from '@/components/ui/Button';
import { DATE_FORMAT } from '@/constants';
import { useBreakpoint } from '@/hooks/use-breakpoints';
import { useContractModalStore } from '@/store/use-contract-modal-store';
import { cn } from '@/utils/cn';

type ColumnsType<T> = TableProps<T>['columns'];

type Props = {
  expandedRowKeys: string[];
  handleExpand: (key: string, event: MouseEvent, expandable: boolean) => void;
  handleEditButtonClick: (contractPersonId: number) => void;
  handleDeleteButtonClick: (contractPersonId: number) => void;
};

export function useAttorneysTable({ expandedRowKeys, handleExpand, handleEditButtonClick, handleDeleteButtonClick }: Props) {
  const { t } = useTranslation('contractAttorneyModal');

  const { setMode } = useContractModalStore();

  const { isBelowMd } = useBreakpoint('md');
  const { isAboveSm, isBelowSm } = useBreakpoint('sm');

  const columns: ColumnsType<ContractPerson> = [
    {
      key: '1',
      dataIndex: ['contractOwner', 'personalCode'],
      title: isAboveSm ? t('personalCode') : t('user'),
      render: (personalCode: string, record) => (
        <div className="flex flex-col">
          <span>{personalCode}</span>
          <span className="md:hidden">
            {record.contractOwner?.firstname} {record.contractOwner?.surname}
          </span>
        </div>
      ),
    },
    {
      key: '2',
      hidden: isBelowMd,
      dataIndex: ['contractOwner'],
      title: t('fullName'),
      render: (contractOwner) => (
        <span>
          {contractOwner.firstname} {contractOwner.surname}
        </span>
      ),
    },
    {
      key: '3',
      width: isBelowMd ? '160px' : 'auto',
      title: t('period', { ns: 'contractsTable' }),
      render: ({ startDate, endDate }) => (
        <span>
          {!startDate && !endDate && t('openEnded')}

          {startDate && !endDate && `${dayjs(startDate).format(DATE_FORMAT)} - ${t('openEnded')}`}

          {!startDate && endDate && `${t('until')} ${dayjs(endDate).format(DATE_FORMAT)}`}

          {startDate && endDate && `${dayjs(startDate).format(DATE_FORMAT)} - ${dayjs(endDate).format(DATE_FORMAT)}`}
        </span>
      ),
      hidden: isBelowSm,
    },
    {
      key: '4',
      title: t('furtherMandateRight'),
      dataIndex: 'furtherMandateRight',
      render: (furtherMandateRight) => t(furtherMandateRight ? 'yes' : 'no', { ns: 'common' }),
      hidden: isBelowSm,
    },
    {
      key: '5',
      width: 136,
      render: (record) => (
        <div className="flex gap-2">
          <Button onClick={() => handleEditButtonClick(record.id)} variant="primary" className="h-8 w-12">
            <Pencil02 className="w-4 h-4 text-white" />
          </Button>
          <Button onClick={() => handleDeleteButtonClick(record.id)} variant="secondaryAlt" className="h-8 w-12">
            <Trash04 className="w-4 h-4 text-white" />
          </Button>
        </div>
      ),
      hidden: isBelowSm,
    },
    {
      key: '6',
      width: 48,
      render: (_, record) => (
        <Button
          onClick={(event) => handleExpand(record.id?.toString(), event, !isBelowSm)}
          className={cn('h-9 w-9 ml-auto flex flex-col items-center justify-center', {
            'bg-brand-green-hover': expandedRowKeys.includes(record.id?.toString()),
          })}
          variant="primary"
        >
          {expandedRowKeys.includes(record.id?.toString()) ? (
            <ChevronDown width={16} height={16} />
          ) : (
            <ChevronRight width={16} height={16} />
          )}
        </Button>
      ),
      hidden: isAboveSm,
    },
  ];

  const expandedRowRender = (record: ContractPerson) => {
    return (
      <div className="sm:hidden flex flex-col divide-y divide-[#DFDFDF] justify-between bg-brand-green-light px-4">
        <div className="flex flex-col py-4">
          <span className="font-medium">{t('period', { ns: 'contractsTable' })}</span>
          <span>
            {!record.startDate && !record.endDate && t('openEnded')}

            {record.startDate && !record.endDate && `${dayjs(record.startDate).format(DATE_FORMAT)} - ${t('openEnded')}`}

            {!record.startDate && record.endDate && `${t('until')} ${dayjs(record.endDate).format(DATE_FORMAT)}`}

            {record.startDate &&
              record.endDate &&
              `${dayjs(record.startDate).format(DATE_FORMAT)} - ${dayjs(record.endDate).format(DATE_FORMAT)}`}
          </span>
        </div>
        <div className="flex flex-col py-4">
          <span className="font-medium">{t('furtherMandateRight')}</span>
          <span>{t(record.furtherMandateRight ? 'yes' : 'no', { ns: 'common' })}</span>
        </div>
        <div className="flex flex-col py-4 gap-4">
          <Button onClick={() => handleEditButtonClick(record.id)} variant="primary">
            <Pencil02 className="w-4 h-4 text-white" />
            <span className="text-white text-[14px]">{t('edit')}</span>
          </Button>
          <Button onClick={() => handleDeleteButtonClick(record.id)} variant="secondaryAlt">
            <Trash04 className="w-4 h-4 text-white" />
            <span className="text-white text-[14px]">{t('delete')}</span>
          </Button>
        </div>
      </div>
    );
  };

  return { columns, expandedRowRender };
}
